import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SendIcon from '@mui/icons-material/Send';
import ReactMarkdown from 'react-markdown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [openReportDialog, setOpenReportDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [feedback, setFeedback] = useState({});

  useEffect(() => {
    const savedReports = JSON.parse(localStorage.getItem('reports') || '[]');
    setReports(savedReports);
  }, []);

  const handleOpenReport = (report) => {
    const rawSections = report.content.split(/(?=### )/g);
    const parsedSections = rawSections.map((section) => {
      const [titleLine, ...rest] = section.split('\n');
      return {
        title: titleLine.replace(/^###\s*/, ''),
        content: rest.join('\n').trim(),
      };
    });

    setSelectedReport({ ...report, sections: parsedSections });
    setFeedback({});
    setOpenReportDialog(true);
  };

  const handleFeedback = (sectionKey, type) => {
    setFeedback((prev) => ({
      ...prev,
      [sectionKey]: {
        ...(prev[sectionKey] || {}),
        status: type,
        note: type === 'disapprove' ? (prev[sectionKey]?.note || '') : '',
      },
    }));
  };

  const handleNoteChange = (sectionKey, note) => {
    setFeedback((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        note,
      },
    }));
  };
  
  const handleSubmitFeedback = async (sectionKey) => {
  const entry = feedback[sectionKey];
  const reportId = selectedReport.id;

  const feedbackText = entry.note || '';
  const redflagId = 'RF01';

  try {
    const feedbackPayload = {
        feedbackText,
        reportId,
        redflagId,
        userDecision: entry.status,
        sectionKey
      };

    const searchResponse = await fetch("http://localhost:7071/api/writeFeedback", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(feedbackPayload),
      });


    if (!searchResponse.ok) {
      const errorText = await searchResponse.text();
      throw new Error(`Search indexing failed: ${errorText}`);
    }

    alert('Feedback submitted successfully!');
  } catch (err) {
    console.error(err);
    alert('Failed to submit feedback: ' + err.message);
  }
};


  const columns = [
    { field: 'case_id', headerName: 'Case ID', flex: 1 },
    { field: 'name', headerName: 'Report Name', flex: 1 },
    {
      field: 'created_at',
      headerName: 'Created Time',
      flex: 1,
      valueFormatter: (params) => {
        const date = new Date(params.value);
        return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => handleOpenReport(params.row)}
        >
          Open
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
        Reports
      </Typography>
      <Box sx={{ width: '100%', maxWidth: '100%' }}>
        <DataGrid
          autoHeight
          rows={reports}
          columns={columns}
          getRowId={(row) => row.id}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          sx={{ width: '100%' }}
        />
      </Box>
      <Dialog
        open={openReportDialog}
        onClose={() => setOpenReportDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Suspicious Transaction Report</DialogTitle>
        <DialogContent>
          {selectedReport?.sections?.map((section, index) => {
            const sectionKey = `section-${index}`;
            const fb = feedback[sectionKey] || {};

            return (
              <Box key={sectionKey} sx={{ mb: 3, p: 2, border: '1px solid #ddd', borderRadius: 2 }}>
                <ReactMarkdown
                    components={{
                      p: ({ node, ...props }) => (
                        <Typography variant="h6" sx={{ mb: 1 }} {...props} />
                      ),
                      strong: ({ node, ...props }) => <strong {...props} />,
                    }}
                  >
                  {section.title}
                </ReactMarkdown>
                <ReactMarkdown
                  components={{
                    h1: ({ node, ...props }) => <Typography variant="h4" sx={{ mt: 2, mb: 1 }} {...props} />,
                    h2: ({ node, ...props }) => <Typography variant="h5" sx={{ mt: 2, mb: 1 }} {...props} />,
                    h3: ({ node, ...props }) => <Typography variant="h6" sx={{ mt: 2, mb: 1 }} {...props} />,
                    p: ({ node, ...props }) => <Typography variant="body1" sx={{ mb: 1 }} {...props} />,
                    ul: ({ node, ...props }) => <Box component="ul" sx={{ pl: 4, mb: 1 }} {...props} />,
                    li: ({ node, ...props }) => <Typography component="li" variant="body1" {...props} />,
                    strong: ({ node, ...props }) => <strong {...props} />,
                    hr: ({ node, ...props }) => <Box sx={{ borderBottom: '1px solid #ccc', my: 2 }} {...props} />,
                  }}
                >
                  {section.content}
                </ReactMarkdown>
                

                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <IconButton
                    onClick={() => handleFeedback(sectionKey, 'approve')}
                    color={fb.status === 'approve' ? 'success' : 'default'}
                  >
                    <CheckCircleIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleFeedback(sectionKey, 'disapprove')}
                    color={fb.status === 'disapprove' ? 'error' : 'default'}
                  >
                    <CancelIcon />
                  </IconButton>
                </Box>

                <Collapse in={fb.status === 'disapprove'}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Why do you disagree?"
                    value={fb.note}
                    onChange={(e) => handleNoteChange(sectionKey, e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <Button
                    variant="outlined"
                    endIcon={<SendIcon />}
                    onClick={() => handleSubmitFeedback(sectionKey)}
                  >
                    Submit Feedback
                  </Button>
                </Collapse>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReportDialog(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Reports;